import React from 'react';
import { Box, Paper, Typography, Chip, Button } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate } from 'react-router-dom';

const SEVERITY_CONFIG = {
    CRITICAL:    { color: '#EF4444', icon: <ErrorOutlineIcon sx={{ fontSize: 14 }} />, label: 'Critical' },
    WARNING:     { color: '#F59E0B', icon: <WarningAmberIcon sx={{ fontSize: 14 }} />, label: 'Warning' },
    OPPORTUNITY: { color: '#10B981', icon: <TipsAndUpdatesIcon sx={{ fontSize: 14 }} />, label: 'Opportunity' },
    INFO:        { color: '#6366F1', icon: <InfoOutlinedIcon sx={{ fontSize: 14 }} />, label: 'Info' },
};

function formatRelativeTime(dateStr) {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
}

export default function InsightCard({ insight, onDismiss, onAskAI }) {
    const navigate = useNavigate();
    const sev = SEVERITY_CONFIG[insight.severity] || SEVERITY_CONFIG.INFO;

    return (
        <Paper
            variant="outlined"
            sx={{
                borderRadius: 2,
                p: 2,
                mb: 1.5,
                borderLeft: `4px solid ${sev.color}`,
                position: 'relative',
            }}
        >
            {/* Top row: severity chip + category chip + timestamp */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <Chip
                    icon={React.cloneElement(sev.icon, { sx: { fontSize: 13, color: `${sev.color} !important` } })}
                    label={sev.label}
                    size="small"
                    sx={{
                        height: 20,
                        fontSize: 10,
                        fontWeight: 700,
                        backgroundColor: `${sev.color}18`,
                        color: sev.color,
                        '& .MuiChip-icon': { color: sev.color },
                    }}
                />
                {insight.category && (
                    <Chip
                        label={insight.category}
                        size="small"
                        sx={{
                            height: 20,
                            fontSize: 10,
                            fontWeight: 500,
                            backgroundColor: '#F1F5F9',
                            color: '#64748B',
                        }}
                    />
                )}
                <Typography
                    sx={{
                        ml: 'auto',
                        fontSize: 11,
                        color: '#94A3B8',
                        flexShrink: 0,
                    }}
                >
                    {formatRelativeTime(insight.generatedAt)}
                </Typography>
            </Box>

            {/* Title */}
            <Typography
                sx={{
                    fontWeight: 700,
                    fontSize: 13,
                    color: '#1E293B',
                    mt: 0.75,
                    lineHeight: 1.4,
                }}
            >
                {insight.title}
            </Typography>

            {/* Description */}
            {insight.description && (
                <Typography sx={{ fontSize: 12, color: '#475569', mt: 0.5, lineHeight: 1.5 }}>
                    {insight.description}
                </Typography>
            )}

            {/* Supporting facts */}
            {insight.supportingFacts && insight.supportingFacts.length > 0 && (
                <Box
                    sx={{
                        bgcolor: '#F8FAFC',
                        border: '1px solid #EEF2F6',
                        borderRadius: 1,
                        p: 1,
                        mt: 1,
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: '#94A3B8',
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            mb: 0.5,
                        }}
                    >
                        Supporting facts
                    </Typography>
                    {insight.supportingFacts.map((fact, i) => (
                        <Typography key={i} sx={{ fontSize: 11, color: '#64748B', lineHeight: 1.6 }}>
                            • {fact}
                        </Typography>
                    ))}
                </Box>
            )}

            {/* Suggested action */}
            {insight.suggestedAction && (
                <Typography
                    sx={{
                        fontSize: 11,
                        color: '#427898',
                        mt: 0.75,
                        fontStyle: 'italic',
                        lineHeight: 1.5,
                    }}
                >
                    → {insight.suggestedAction}
                </Typography>
            )}

            {/* Action row */}
            <Box sx={{ display: 'flex', gap: 0.5, mt: 1.25, flexWrap: 'wrap' }}>
                {insight.entityPath && (
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => navigate(insight.entityPath)}
                        sx={{
                            fontSize: 11,
                            py: 0.25,
                            px: 1,
                            minHeight: 24,
                            borderColor: '#E2E8F0',
                            color: '#475569',
                            '&:hover': { borderColor: '#427898', color: '#427898' },
                        }}
                    >
                        View
                    </Button>
                )}
                <Button
                    size="small"
                    variant="outlined"
                    onClick={() => onAskAI(insight)}
                    sx={{
                        fontSize: 11,
                        py: 0.25,
                        px: 1,
                        minHeight: 24,
                        borderColor: '#E2E8F0',
                        color: '#427898',
                        '&:hover': { borderColor: '#427898', bgcolor: '#EFF6FF' },
                    }}
                >
                    Ask AI
                </Button>
                <Button
                    size="small"
                    variant="outlined"
                    onClick={() => onDismiss(insight.id)}
                    sx={{
                        fontSize: 11,
                        py: 0.25,
                        px: 1,
                        minHeight: 24,
                        borderColor: '#E2E8F0',
                        color: '#94A3B8',
                        '&:hover': { borderColor: '#CBD5E1', color: '#64748B' },
                    }}
                >
                    Dismiss
                </Button>
            </Box>
        </Paper>
    );
}
